import { pdfText } from '../../lib/pdfjs';
import type { LohnKind } from './types';

// Lohnunterlagen erkennen (Sam 11.10.: „ich lade mehrere pdfs hoch und der soll dann erkennen was zahlungsliste ist was
// abrechnung ist und und und“). Ohne KI: Der Browser liest den Text der ersten zwei Seiten mit pdf.js, dazu zählt der
// Dateiname. Lohndaten verlassen dafür den Rechner nicht. Gescannte PDFs ohne Text und Fotos erkennt nur der Dateiname.
// Was nicht eindeutig ist, wählt die Kanzlei von Hand.

// Stichwörter je Art, schon in der Form von `compact`. Gewicht 3: eindeutige Überschrift, 1: kommt auch anderswo vor.
const RULES: { kind: LohnKind; words: [string, number][] }[] = [
  {
    kind: 'abrechnungen',
    words: [
      ['bruttonettobezuege', 3], // DATEV: „Abrechnung der Brutto/Netto-Bezüge“
      ['bruttonettoabrechnung', 3],
      ['verdienstabrechnung', 3],
      ['entgeltabrechnung', 3],
      ['gehaltsabrechnung', 3],
      ['lohnabrechnung', 1],
      ['auszahlungsbetrag', 1],
    ],
  },
  {
    kind: 'zahlungsliste',
    words: [
      ['zahlungsliste', 3],
      ['ueberweisungsliste', 3],
      ['zahlungsuebersicht', 3],
      ['auszahlungsliste', 3],
      ['zahlungsanweisung', 3],
      ['sammelueberweisung', 3],
    ],
  },
  {
    kind: 'beitragsnachweise',
    words: [
      ['beitragsnachweis', 3],
      ['beitragsabrechnung', 3],
    ],
  },
  {
    kind: 'lst_anmeldung',
    words: [
      ['lohnsteueranmeldung', 3],
      ['lstanmeldung', 3],
      ['anmeldungderlohnsteuer', 3],
    ],
  },
  {
    kind: 'lohnjournal',
    words: [
      ['lohnjournal', 3],
      ['gehaltsjournal', 3],
      ['entgeltjournal', 3],
      ['abrechnungsjournal', 3],
    ],
  },
  {
    kind: 'buchungsliste',
    words: [
      ['buchungsliste', 3],
      ['buchungsbeleg', 3],
      ['lohnbuchungen', 3],
      ['buchungsjournal', 3],
    ],
  },
  {
    kind: 'sonstige',
    words: [
      ['lohnkonto', 3],
      ['arbeitgeberaufwand', 3],
      ['lohnartenuebersicht', 3],
      ['kostenstellenuebersicht', 3],
      ['meldebescheinigung', 3],
    ],
  },
];

// Kleinbuchstaben ohne Umlaute, Leer- und Satzzeichen: „Lohnsteuer-Anmeldung“, „Lohnsteuer Anmeldung“ und gesperrt
// gesetztes „L o h n j o u r n a l“ werden gleich.
export function compact(s: string): string {
  return s
    .normalize('NFC')
    .toLowerCase()
    .replace(/ä/g, 'ae')
    .replace(/ö/g, 'oe')
    .replace(/ü/g, 'ue')
    .replace(/ß/g, 'ss')
    .replace(/[^a-z0-9]/g, '');
}

// Kopf der ersten Seite (Überschrift) zählt doppelt, der Dateiname dreifach (den hat jemand mit Absicht so genannt)
const HEAD = 400;

export function guessKind(fileName: string, text: string): LohnKind | null {
  const name = compact(fileName.replace(/\.[a-z0-9]+$/i, ''));
  const body = compact(text);
  const scores = RULES.map(({ kind, words }) => {
    let score = 0;
    for (const [w, weight] of words) {
      if (name.includes(w)) score += 3 * weight;
      const at = body.indexOf(w);
      if (at >= 0) score += at < HEAD ? 2 * weight : weight;
    }
    return { kind, score };
  }).sort((a, b) => b.score - a.score);
  const [best, next] = scores;
  if (!best || best.score < 3 || best.score === next?.score) return null;
  return best.kind;
}

// Art einer Datei; null, wenn nichts eindeutig passt
export async function recognize(file: File): Promise<LohnKind | null> {
  let text = '';
  if (file.type === 'application/pdf' || /\.pdf$/i.test(file.name)) {
    try {
      text = await pdfText(await file.arrayBuffer());
    } catch {
      // kaputt oder mit Passwort: nur der Dateiname
    }
  }
  return guessKind(file.name, text);
}
